import { useEffect, useState } from 'react';

/**
 * Detect whether a camera can be opened for capture.
 * Returns null while probing, then true/false.
 */
export function useCameraAvailable(): boolean | null {
  const [available, setAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function probe() {
      if (typeof navigator === 'undefined') {
        if (!cancelled) setAvailable(false);
        return;
      }
      const md = navigator.mediaDevices;
      if (!md || typeof md.getUserMedia !== 'function') {
        if (!cancelled) setAvailable(false);
        return;
      }
      try {
        if (typeof md.enumerateDevices === 'function') {
          const devices = await md.enumerateDevices();
          const hasVideo = devices.some((d) => d.kind === 'videoinput');
          // Before permission, label may be empty but kind is still videoinput.
          // If no devices listed yet, still allow trying getUserMedia on mobile.
          if (hasVideo) {
            if (!cancelled) setAvailable(true);
            return;
          }
          // Some browsers hide devices until permission — treat as available if getUserMedia exists
          // only when the UA looks like a phone/tablet with typical camera hardware.
          const ua = navigator.userAgent || '';
          const mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
          if (!cancelled) setAvailable(mobile);
          return;
        }
        if (!cancelled) setAvailable(true);
      } catch {
        if (!cancelled) setAvailable(false);
      }
    }

    void probe();
    return () => { cancelled = true; };
  }, []);

  return available;
}
